import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, MapPin, ShieldCheck, LogOut, Sun, Moon } from "lucide-react";
import api, { LOGO_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import CitySelector from "./CitySelector";
import NotificationBell from "./NotificationBell";

function initials(name) {
  return (name || "B").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

export default function Header({ onOpenAuth, onOpenSearch }) {
  const { user, logout, city } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data.categories || [])); }, []);

  const nav = [
    { label: "Explore", to: "/browse", id: "explore" },
    ...categories.slice(0, 4).map((c) => ({ label: c.label, to: `/${c.slug}`, id: c.label.toLowerCase().replace(/\s+/g, "-"), wide: true })),
    { label: "How BHEGA Works", to: "/how-it-works", id: "how-bhega-works" },
    { label: "About Us", to: "/about", id: "about-us" },
  ];
  const linkCls = ({ isActive }) =>
    `shrink-0 whitespace-nowrap px-3 h-[64px] inline-flex items-center text-[13.5px] font-semibold border-b-[3px] transition-colors ${
      isActive ? "border-[#FFBE06] bg-raised text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-paper-2"
    }`;
  const isAdmin = user?.role === "admin";

  return (
    <header className="sticky top-0 z-40 glass border-b border-border" data-testid="bhega-header">
      <div className="container-x">
        <div className="flex items-center h-[64px] gap-2 sm:gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="bhega-logo-link" aria-label="BHEGA home">
            <img src={LOGO_URL} alt="" className="h-9 w-9 object-contain" />
            <span className="font-extrabold tracking-[-0.04em] text-[19px] text-foreground hidden sm:inline">BHEGA</span>
          </Link>

          <nav className="hidden lg:flex items-center flex-1 min-w-0 overflow-x-auto no-scrollbar ml-4" aria-label="Main">
            {nav.filter((n) => !n.wide).map((n) => (
              <NavLink key={n.to} to={n.to} end className={linkCls} data-testid={`nav-${n.id}`}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex-1 lg:hidden" />

          <button
            onClick={onOpenSearch}
            data-testid="header-search-trigger"
            aria-label="Search products (Ctrl+K)"
            className="hidden md:inline-flex items-center gap-2 h-10 px-3 2xl:min-w-[220px] border border-border bg-card text-muted-foreground text-[13px] hover:border-[var(--bhega-border-strong)] transition-colors"
          >
            <Search size={15} />
            <span className="flex-1 text-left hidden 2xl:inline">Search products…</span>
            <kbd className="text-xs font-mono border border-border px-1.5 py-0.5 hidden 2xl:inline">⌘K</kbd>
          </button>

          <CitySelector />

          <button onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} className="icon-btn hidden md:inline-flex">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button onClick={toggleTheme} data-testid="mobile-theme-toggle" aria-label="Toggle theme" className="icon-btn md:hidden">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {user && !isAdmin && <NotificationBell />}

          {user ? (
            <div className="flex items-center gap-2">
              {isAdmin && (
                <div className="hidden md:flex items-center gap-1.5">
                  <button data-testid="admin-dashboard-link" onClick={() => navigate("/admin")} className="btn-dark text-xs"><ShieldCheck size={13} /> Admin</button>
                  <button data-testid="admin-hire-link" onClick={() => navigate("/admin/hire-requests")} className="btn-primary text-xs">Hires</button>
                </div>
              )}
              <button
                data-testid="user-profile-link"
                onClick={() => navigate("/me")}
                className="hidden md:inline-flex items-center gap-2 h-10 pl-1 pr-3 border border-border bg-card hover:border-[var(--bhega-border-strong)] transition-colors"
                aria-label="Your profile and rooms"
              >
                <span className="avatar-sq w-8 h-8 bg-[#111111] text-[#FFBE06] dark:bg-[#FFBE06] dark:text-[#111111]">{initials(user.name)}</span>
                <span className="max-w-[110px] truncate text-[13px] font-semibold">{user.name || user.phone_number}</span>
              </button>
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="icon-btn hidden md:inline-flex" aria-label="Sign out" title="Sign out">
                <LogOut size={16} />
              </button>
              <button data-testid="mobile-logout-btn" onClick={() => { logout(); navigate("/"); }} className="icon-btn md:hidden" aria-label="Sign out">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <>
              <button data-testid="header-signin-btn" onClick={onOpenAuth} className="btn-primary hidden md:inline-flex">Sign in</button>
              <button data-testid="mobile-signin-btn" onClick={onOpenAuth} className="btn-primary text-xs md:hidden">Sign in</button>
            </>
          )}
        </div>

        <div className="lg:hidden -mx-1 px-1 pb-2.5 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end className={({ isActive }) => `chip shrink-0 ${isActive ? "chip-active" : ""}`} data-testid={`mnav-chip-${n.id}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>
      {city && (
        <div className="border-t border-border bg-paper-2 text-xs px-4 py-1.5 text-center font-medium text-[var(--bhega-text)] truncate" data-testid="active-city-banner">
          <MapPin size={11} className="inline mr-1 -mt-0.5 text-brand" />
          Showing rooms and buyers in <strong className="text-foreground">{city}</strong>
        </div>
      )}
    </header>
  );
}
